import React from 'react';
import InvalidOrgPage from 'pages/invalid-org';

/**
 * Short-circuits app rendering when the current hostname's subdomain is
 * in VITE_BLOCKED_ORGS (CSV, case-insensitive).  Used to disable specific
 * orgs on specific environments without per-environment code branching —
 * e.g. on the sandbox env, set VITE_BLOCKED_ORGS=manulife so manulife.<sbx>
 * renders the InvalidOrgPage instead of the normal app shell.
 *
 * Backend mirrors this with `BLOCKED_TENANT_SLUGS` so blocked orgs also
 * fail on the API layer if the frontend is bypassed.
 */
const parseBlocked = () => {
  const raw = (import.meta?.env?.VITE_BLOCKED_ORGS ?? '').toString();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );
};

const extractSubdomain = (hostname) => {
  if (!hostname) return '';
  const host = hostname.split(':', 1)[0].trim().toLowerCase();
  if (!host || !host.includes('.')) return '';
  return host.split('.', 1)[0];
};

const InvalidOrgGate = ({ children }) => {
  // Compute once on mount — VITE_ env vars are baked at build time and the
  // hostname can't change without a navigation, so re-checking on every
  // render is wasted work.
  const blockedSubdomain = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    const blocked = parseBlocked();
    if (blocked.size === 0) return null;
    const sub = extractSubdomain(window.location.hostname);
    return sub && blocked.has(sub) ? sub : null;
  }, []);

  if (blockedSubdomain) {
    return <InvalidOrgPage subdomain={blockedSubdomain} />;
  }
  return children;
};

export default InvalidOrgGate;
